import { useRollbar } from '@rollbar/react'
import {
  BadRequest,
  InternalError,
  NetworkError,
  ServiceUnavailable,
} from 'common/errors'
import { BookingPaidCalendarInterface } from 'common/types/entities/booking-paid-calendar-interface'
import { OldEntityInterface } from 'common/types/entities/OldEntityInterface'
import { TwoStepPaymentFormStepInterface } from 'common/types/entities/two-step-payment-form-interface'
import { optInFail } from 'publisher/actions/optInActions'
import * as api from 'publisher/api/paypal'
import usePage, { selectors as pageSelectors } from 'publisher/hooks/usePage'
import usePayment, {
  selectors as paymentSelectors,
} from 'publisher/hooks/usePayment'
import loadScript from 'publisher/utils/loadScript'
import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { PaymentButtonEntityInterface } from 'src/shared/entities/payment-button/payment-button.schema'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import usePaymentSubmit from './usePaymentSubmit'

export default function usePaypalPaymentHandler(
  entity:
    | OldEntityInterface
    | PaymentButtonEntityInterface
    | BookingPaidCalendarInterface
    | TwoStepPaymentFormStepInterface,
) {
  const dispatch = useDispatch()
  const { t } = useTypedTranslation()
  const rollbar = useRollbar()
  const { errors, setErrors, isLoading, submit } = usePaymentSubmit(entity)
  const [isScriptLoading, setScriptLoading] = useState(true)
  const pageId = usePage(pageSelectors.getPageId)
  const purchaseProcessId = usePayment(paymentSelectors.getPurchaseProcessId)

  useEffect(() => {
    if (window.paypal !== undefined) return

    loadScript(
      'https://www.paypalobjects.com/api/checkout.min.js',
      'paypal-js',
      () => {
        //@ts-expect-error
        window.paypal.checkout.setup('merchant', {
          environment: process.env.PAYPAL_ENV,
        })
        setScriptLoading(false)
      },
    )
  }, [])

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault()
    submit(async body => {
      try {
        await api.validateForm(pageId, body, purchaseProcessId)
      } catch (error) {
        if (error instanceof BadRequest) {
          setErrors([
            ...error.response.data.errors.common,
            t('core.errors.bad_request'),
          ])
          dispatch(optInFail({ fields: error.response.data.errors.fields }))
        } else if (error instanceof ServiceUnavailable) {
          setErrors([t('core.errors.service_temporary_unavailable')])
        } else if (error instanceof NetworkError) {
          setErrors([t('core.errors.no_connection')])
        } else if (error instanceof InternalError) {
          setErrors([t('core.error.title')])
        } else {
          rollbar.captureEvent(body, 'debug')
          rollbar.error('Paypal SOAP validateForm error', error as Error)
        }

        // initializes paypal payment modal
        // @ts-expect-error
        window.paypal.checkout.initXO()

        // @ts-expect-error
        window.paypal.checkout.closeFlow()
        return
      }

      try {
        const { data } = await api.purchaseDetails(
          pageId,
          body,
          purchaseProcessId,
        )

        if (data.redirect) {
          // @ts-expect-error
          window.paypal.checkout.closeFlow()
          window.location.assign(data.redirect)
          return
        }

        // @ts-expect-error
        window.paypal.checkout.startFlow(data.approvalUrl)
      } catch (error) {
        if (error instanceof BadRequest) {
          setErrors([
            ...error.response.data.errors.common,
            t('core.errors.bad_request'),
          ])
          dispatch(optInFail({ fields: error.response.data.errors.fields }))
        } else if (error instanceof ServiceUnavailable) {
          setErrors([t('core.errors.service_temporary_unavailable')])
        } else if (error instanceof NetworkError) {
          setErrors([t('core.errors.no_connection')])
        } else if (error instanceof InternalError) {
          setErrors([t('core.error.title')])
        } else {
          setErrors([t('core.error.title')])
          rollbar.captureEvent(body, 'debug')
          rollbar.error('Paypal SOAP error', error as Error)
        }
        // @ts-expect-error
        window.paypal.checkout.closeFlow()
      }
    })
  }

  return { errors, isLoading, handleSubmit, isScriptLoading }
}
